import { MiDataTableOptions } from '@/components/MiDataTable/MiDataTable';
import { MiFormField } from '@/components/MiForm/MiForm';
import { Report } from '@/models/Report';
import { ReportStatus } from '@/support/ReportStatus';
import { DateTime } from 'luxon';
import { Component, Vue } from 'vue-property-decorator';

@Component<Dashboard>({ })
export default class Dashboard extends Vue {
  protected $pageTitle = 'Dashboard';

  protected isLoading = true;

  protected showExpired = false; // For garantietermijn

  protected showExpiredAppeal = false; // For bezwaartermijn

  protected isExpired(date: string): boolean {
    const x = new Date(date);
    const y = new Date(DateTime.local().toFormat('yyyy-LL-dd HH:mm'));

    return + x <= + y;
  }

  protected appointmentIsScheduled(date: string): boolean {
    return date !== '00-00-0000 00:00';
  }

  protected getWarrantyPeriodColor(days: string): string {
    const parsedDays = Number(days);
    if (parsedDays <= 3) {
      return 'warning';
    } if (parsedDays > 3 && parsedDays <= 7) {
      return 'pending';
    }
    return 'success';
  }

  protected get filterFields(): MiFormField[] {
    return [
      {
        type: 'checkbox',
        key: 'archived',
        label: 'Toon gearchiveerde opdrachten',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get expiringFilters(): Record<string, any> {
    let filters = {};

    if (this.showExpired) {
      filters = {
        ...filters,
        ...{ liable_deadline: { to: DateTime.local().toFormat('yyyy-LL-dd HH:mm') } },
      };
    }

    if (! this.showExpired) {
      filters = {
        ...filters,
        ...{
          liable_deadline: {
            from: DateTime.local().toFormat('yyyy-LL-dd HH:mm'),
            to: DateTime.local().plus({ months: 2 }).toFormat('yyyy-LL-dd HH:mm'),
          },
        },
      };
    }

    if (this.$store.getters.isContractor) {
      filters = { ...filters, ...this.contractorFilters };
    }

    return filters;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get appealFilters(): Record<string, any> {
    let filters = {};

    if (this.showExpiredAppeal) {
      filters = {
        ...filters,
        ...{ appeal_deadline: { to: DateTime.local().toFormat('yyyy-LL-dd HH:mm') } },
      };
    }

    if (! this.showExpiredAppeal) {
      filters = {
        ...filters,
        ...{
          appeal_deadline: {
            from: DateTime.local().toFormat('yyyy-LL-dd HH:mm'),
            to: DateTime.local().plus({ months: 2 }).toFormat('yyyy-LL-dd HH:mm'),
          },
        },
      };
    }

    if (this.$store.getters.isContractor) {
      filters = { ...filters, ...this.contractorFilters };
    }

    return filters;
  }

  protected get expiringReportsOptions(): MiDataTableOptions {
    return {
      model: new Report()
        .filter(this.expiringFilters),
      limit: 5,
      compact: true,
      noDataText: 'Er zijn geen aflopende garantietermijnen',
      noResultsText: 'Er zijn geen resultaten gevonden',
      headers: [
        {
          text: 'Zaaknummer',
          value: 'case_number',
        },
        {
          text: 'IMG Zaaknummer',
          value: 'img_case_number',
          sortable: false,
        },
        {
          text: 'Aantal dagen tot deadline',
          value: 'days_until_liable_deadline',
        },
      ],
    };
  }

  protected get appealReportsOptions(): MiDataTableOptions {
    return {
      model: new Report()
        .filter(this.appealFilters),
      limit: 5,
      compact: true,
      noDataText: 'Er zijn geen aflopende bezwaartermijnen',
      noResultsText: 'Er zijn geen resultaten gevonden',
      headers: [
        {
          text: 'Zaaknummer',
          value: 'case_number',
        },
        {
          text: 'IMG Zaaknummer',
          value: 'img_case_number',
          sortable: false,
        },
        {
          text: 'Aantal dagen tot deadline',
          value: 'days_until_appeal_deadline',
        },
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get waitingForSignatureFilters(): Record<string, any> {
    let filters = {};

    filters = {
      ...filters,
      ...{ statuses: [ReportStatus.WAITING_FOR_SIGNATURE] },
    };

    if (this.$store.getters.isContractor) {
      filters = { ...filters, ...this.contractorFilters };
    }

    return filters;
  }

  protected get waitingForSignatureOptions(): MiDataTableOptions {
    return {
      model: new Report().filter(this.waitingForSignatureFilters),
      limit: 5,
      compact: true,
      noDataText: this.$store.getters.isBureau ? 'Er zijn geen opdrachten in afwachting op ondertekening' : 'Er zijn geen te accepteren opdrachten',
      noResultsText: 'Er zijn geen resultaten gevonden',
      headers: [
        {
          text: 'Zaaknummer',
          value: 'case_number',
        },
        {
          text: 'IMG Zaaknummer',
          value: 'img_case_number',
          sortable: false,
        },
        {
          text: 'Aantal dagen tot deadline',
          value: 'days_until_signature_deadline',
        },
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get upcomingStartDatesFilters(): Record<string, any> {
    let filters = {};

    filters = {
      ...filters,
      ...{
        first_appointment: {
          from: DateTime.local().toFormat('yyyy-LL-dd HH:mm'),
          to: DateTime.local().plus({ months: 2 }).toFormat('yyyy-LL-dd HH:mm'),
        },
      },
    };

    if (this.$store.getters.isContractor) {
      filters = { ...filters, ...this.contractorFilters };
    }

    return filters;
  }

  protected get upcomingStartDatesOptions(): MiDataTableOptions {
    return {
      model: new Report()
        .filter(this.upcomingStartDatesFilters),
      limit: 5,
      compact: true,
      noDataText: 'Er zijn geen aankomende startmomenten',
      noResultsText: 'Er zijn geen resultaten gevonden',
      headers: [
        {
          text: 'Zaaknummer',
          value: 'case_number',
        },
        {
          text: 'IMG Zaaknummer',
          value: 'img_case_number',
          sortable: false,
        },
        {
          text: 'Start datum',
          value: 'first_appointment',
        },
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get upComingDeliveryAppointmentsFilters(): Record<string, any> {
    let filters = {};

    filters = {
      ...filters,
      ...{
        delivery_appointment: {
          from: DateTime.local().toFormat('yyyy-LL-dd HH:mm'),
          to: DateTime.local().plus({ months: 2 }).toFormat('yyyy-LL-dd HH:mm'),
        },
      },
    };

    if (this.$store.getters.isContractor) {
      filters = { ...filters, ...this.contractorFilters };
    }

    return filters;
  }

  protected get upComingDeliveryAppointmentsOptions(): MiDataTableOptions {
    return {
      model: new Report()
        .filter(this.upComingDeliveryAppointmentsFilters),
      limit: 5,
      compact: true,
      noDataText: 'Er zijn geen aankomende oplevermomenten',
      noResultsText: 'Er zijn geen resultaten gevonden',
      headers: [
        {
          text: 'Zaaknummer',
          value: 'case_number',
        },
        {
          text: 'IMG Zaaknummer',
          value: 'img_case_number',
          sortable: false,
        },
        {
          text: 'oplevermoment',
          value: 'delivery_appointment',
        },
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get contractorFilters(): Record<string, any> {
    return {
      contractor: this.$store.getters.user.contractor.uuid,
    };
  }
}
